/**
 * TusCourses PWA - IndexedDB Database
 *
 * Uses Dexie.js for offline data storage and sync queue
 */

import Dexie, { type Table } from 'dexie'
import type {
  CourseDto,
  CourseSessionDto,
  ParticipantDto,
  CourseRegistrationDto,
  InstructorSummaryDto,
} from '@/types/api.types'
import type {
  LocalAttendance,
  LocalGuest,
  SyncQueueItem,
  PhotoThumbnail,
  PhotoUploadQueueItem,
} from '@/types/local.types'

/**
 * TusCourses Database
 */
export class TusCoursesDB extends Dexie {
  // Cached data from API (read-only locally)
  courses!: Table<CourseDto, number>
  sessions!: Table<CourseSessionDto, number>
  participants!: Table<ParticipantDto, number>
  registrations!: Table<CourseRegistrationDto, number> // id as primary key
  instructors!: Table<InstructorSummaryDto, number>

  // Local data with sync support
  attendances!: Table<LocalAttendance, number>
  guests!: Table<LocalGuest, number>

  // Sync queue for pending changes
  syncQueue!: Table<SyncQueueItem, number>

  // Photo support
  photoThumbnails!: Table<PhotoThumbnail, number>
  photoUploadQueue!: Table<PhotoUploadQueueItem, number>

  constructor() {
    super('TusCoursesDB')

    // Database schema
    // Version 1: Original schema
    this.version(1).stores({
      courses: 'id, title, number, updatedAt',
      sessions: 'id, courseId, date, updatedAt',
      participants: 'id, memberNumber, [firstName+lastName], updatedAt',
      registrations: '[courseId+participantId], courseId, participantId',
      attendances: '++id, localId, courseSessionId, personId, syncStatus, updatedAt',
      guests: '++id, localId, sessionId, courseId, syncStatus, createdAt',
      syncQueue: '++id, type, action, localId, createdAt, retryCount',
    })

    // Version 2: Changed registrations primary key from composite to id
    // Delete and recreate registrations table (it's just cached data)
    this.version(2).stores({
      registrations: null, // Delete old table
    })

    this.version(3).stores({
      registrations: 'id, courseId, participantId', // Recreate with new primary key
    })

    // Version 4: Add instructors table for offline cache
    this.version(4).stores({
      instructors: 'id, name',
    })

    // Version 5: Add photo tables
    this.version(5).stores({
      photoThumbnails: 'personId',
      photoUploadQueue: '++id, personId',
    })
  }

  /**
   * Clear all cached data (not sync queue)
   */
  async clearCache(): Promise<void> {
    await this.transaction(
      'rw',
      [
        this.courses,
        this.sessions,
        this.participants,
        this.registrations,
        this.instructors,
        this.photoThumbnails,
      ],
      async () => {
        await this.courses.clear()
        await this.sessions.clear()
        await this.participants.clear()
        await this.registrations.clear()
        await this.instructors.clear()
        await this.photoThumbnails.clear()
      }
    )
  }

  /**
   * Clear everything including sync queue
   */
  async clearAll(): Promise<void> {
    await this.transaction('rw', this.tables, async () => {
      for (const table of this.tables) {
        await table.clear()
      }
    })
  }

  /**
   * Get pending sync items count
   */
  async getPendingCount(): Promise<number> {
    const [syncCount, photoCount] = await Promise.all([
      this.syncQueue.count(),
      this.photoUploadQueue.count(),
    ])
    return syncCount + photoCount
  }

  /**
   * Get failed sync items
   */
  async getFailedItems(): Promise<SyncQueueItem[]> {
    return await this.syncQueue.where('retryCount').above(2).toArray()
  }
}

// Singleton instance
export const db = new TusCoursesDB()

// Export for testing
export default db
